export const INSURER_CREDIT_AGING_BUCKETS = [
  "0-30",
  "31-60",
  "61-90",
  "90+"
] as const;

export type InsurerCreditAgingBucket =
  (typeof INSURER_CREDIT_AGING_BUCKETS)[number];

// Keyed by bucket label. Read through `agingBucketAmount` rather than indexing
// directly, so a bucket the backend adds later degrades to zero instead of NaN.
export type InsurerCreditAgingBuckets = Record<string, string>;

export const INSURER_CREDIT_ENTRY_TYPE = {
  CREDIT: "CREDIT",
  APPLICATION: "APPLICATION",
  ADJUSTMENT: "ADJUSTMENT",
  REVERSAL: "REVERSAL"
} as const;

export type InsurerCreditEntryType =
  (typeof INSURER_CREDIT_ENTRY_TYPE)[keyof typeof INSURER_CREDIT_ENTRY_TYPE];

export const INSURER_CREDIT_REASON_CODE = {
  DEVICE_RETURN: "device_return",
  CANCELLATION: "cancellation",
  OVERPAYMENT: "overpayment",
  TAKE_BACK: "take_back",
  BACKFILL: "backfill",
  // SN-19335 added this on the backend for "the insurer told us to stop holding this", which is a
  // different event from "we keyed it wrong" (correction). It has to be listed here or the report
  // renders the raw code and the reason filter cannot select it.
  WRITE_OFF: "write_off",
  CORRECTION: "correction"
} as const;

export type InsurerCreditReasonCode =
  (typeof INSURER_CREDIT_REASON_CODE)[keyof typeof INSURER_CREDIT_REASON_CODE];

export interface PaginatedEnvelope<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface InsurerCreditBalanceRow {
  insurer_id: number;
  insurer_name: string;
  balance: string;
  aging_buckets: InsurerCreditAgingBuckets;
  flagged_amount: string;
  // Comes straight from the API's FIFO pass. The 60-day clock is deliberately
  // not the bucket edge — money held exactly 60 days is flagged while it still
  // sits in "31-60" — so this must never be re-derived from `aging_buckets`.
  is_flagged: boolean;
  flagged_age_days: number;
  oldest_credit_date: string | null;
  days_held_oldest: number | null;
  last_activity_at: string;
  as_of_date: string;
  // IANA zone of the practice. Every figure on this report is anchored there — `as_of_date` is
  // today in it and the aging windows start at midnight in it — so a timestamp must be rendered in
  // it too, never in the viewer's browser zone, or the date drifts a day away from the arithmetic.
  timezone: string;
}

// `usePagination` de-duplicates accumulated pages by `item.id`. Balance rows are
// keyed by insurer and carry no `id`, so the provider stamps one on before the
// rows reach the hook — without it every page after the first is dropped.
export type InsurerCreditBalanceListRow = InsurerCreditBalanceRow & {
  id: number;
};

export interface InsurerCreditAgingSummary {
  as_of_date: string;
  timezone: string;
  total_balance: string;
  insurer_count: number;
  aging_buckets: InsurerCreditAgingBuckets;
  flagged_age_days: number;
  flagged_amount: string;
  flagged_insurer_count: number;
}

export interface InsurerCreditLedgerEntryRow {
  id: number;
  created_at: string;
  timezone: string;
  entry_type: InsurerCreditEntryType;
  reason_code: InsurerCreditReasonCode | "";
  amount: string;
  reference_text: string;
  insurer_id: number;
  insurer_name: string;
  clinic_id: number | null;
  clinic_name: string | null;
  created_by_id: number | null;
  created_by_name: string;
  target_patient_id: number | null;
  target_patient_name: string;
  target_sale_id: number | null;
  source_payment_id: number | null;
  source_refund_id: number | null;
  reversed_entry_id: number | null;
}

// `refreshKey` is injected by `usePagination` and is not an API parameter — the
// service strips it before building the request.
type PaginatedQueryArgs = {
  limit?: number;
  offset?: number;
  refreshKey?: number;
};

export type InsurerCreditBalancesFilters = {
  insurer?: string;
  flagged_only?: boolean;
  include_zero_balance?: boolean;
};

export type InsurerCreditBalancesQuery = InsurerCreditBalancesFilters &
  PaginatedQueryArgs;

export type InsurerCreditLedgerEntriesFilters = {
  insurer?: string;
  clinic?: string;
  user?: string;
  entry_type?: string;
  reason_code?: string;
  after_date?: string;
  before_date?: string;
};

export type InsurerCreditLedgerEntriesQuery =
  InsurerCreditLedgerEntriesFilters & PaginatedQueryArgs;

// Only the keys in the backend's EXPORT_FILTER_PARAMS allowlist reach the async
// export task; `title` and `current_clinic` name the generated file instead.
export type InsurerCreditLedgerEntriesExportQuery =
  InsurerCreditLedgerEntriesFilters & {
    title?: string;
    current_clinic?: number;
  };
